import { Dialog, Grid } from "@mui/material"
import { ReportUploadRequest, ReportUploadRequestFile, ReportUploadRequestStatus } from "../../../../types/reports"
import { useEffect, useMemo, useState } from "react"
import axios from "utils/axios"
import ScrollX from "components/ScrollX"
import MainCard from "components/MainCard"
import ReactTable from "components/ReactTable"
import reportsTableColumns from "../reportsTableColumns"
import LoaderFullPage from "components/LoaderFullPage"
import { enqueueSnackbar } from "notistack"
import { KSpan, KTitleSpan, KDropdown, KButton, KTooltip } from "kahuna-base-react-components"
import "../styles/report-upload-requests.css"
import { ReportUploadColumnTypes } from "utils/constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import ApproveModal from "./ApproveModal"
import InfoIcon from "../../../../assets/images/icons/info-circle.svg"

interface Props {
  reportUploadRequestList: ReportUploadRequest[]
  setReportUploadRequestList: (reportUploadRequestList: ReportUploadRequest[]) => void
  reportUploadRequest?: ReportUploadRequest
  setReportUploadRequest: (reportUploadRequest?: ReportUploadRequest) => void
  reportUploadColumnsMap: { [key: string]: string[] }
}

const initialMatchedHeaders = {
  [ReportUploadColumnTypes.PERIOD]: "",
  [ReportUploadColumnTypes.PLATFORM]: "",
  [ReportUploadColumnTypes.TRANSACTION_DATE]: "",
  [ReportUploadColumnTypes.SONG_TITLE]: "",
  [ReportUploadColumnTypes.ISRC]: "",
  [ReportUploadColumnTypes.TERRITORY]: "",
  [ReportUploadColumnTypes.QUANTITY]: "",
  [ReportUploadColumnTypes.CURRENCY]: "",
  [ReportUploadColumnTypes.SEGMENT]: "",
  [ReportUploadColumnTypes.SEGMENT_DESCRIPTION]: "",
  [ReportUploadColumnTypes.NET_QUANTITY]: "",
  [ReportUploadColumnTypes.GROSS_PROGRESS]: "",
  [ReportUploadColumnTypes.NET_PROGRESS]: "",
  [ReportUploadColumnTypes.SPLIT_RATE]: "",
  description: "", // this is only for explaining the edge cases, it is not a header only will be added to ReportUploadRequestSheet modal's heading_map column.
  selected_currency: ""
}

const currencyOptions: KSelectOption[] = [
  { label: "TRY", value: 1, value2: "TRY" },
  { label: "USD", value: 2, value2: "USD" },
  { label: "GBP", value: 3, value2: "GBP" },
  { label: "EUR", value: 4, value2: "EUR" }
]

const ReportUploadRequestModal = ({
  reportUploadRequestList,
  setReportUploadRequestList,
  reportUploadRequest,
  setReportUploadRequest,
  reportUploadColumnsMap
}: Props) => {
  const [files, setFiles] = useState<ReportUploadRequestFile[]>([]) // spreadsheet files of the selected folder
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingApprove, setLoadingApprove] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<ReportUploadRequestFile>()
  const [matchedHeaders, setMatchedHeaders] = useState<{ [key: string]: typeof initialMatchedHeaders }>({})
  const [preMappedMatchedHeaders, setPreMappedMatchedHeaders] = useState<{
    [key: string]: typeof initialMatchedHeaders
  }>({})
  const [selectOptions, setSelectOptions] = useState<{ [key: string]: KSelectOption[] }>({})
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  const [preMappedGroupList, setPreMappedGroupList] = useState<string[]>([])
  const [confirmType, setConfirmType] = useState<"approve" | "disapprove" | undefined>(undefined)

  const fetchFiles = (driveFolderId: string) => {
    setLoading(true)
    axios(`/reports/report-upload-request/list_upload_request_sheets/?drive_folder_id=${driveFolderId}`, {
      method: "get"
    })
      .then((resp) => {
        if (resp.data && resp.data.results) {
          const spreadSheets: ReportUploadRequestFile[] = resp.data.results
            .map((item: any, index: number) => {
              const file: ReportUploadRequestFile = {
                id: index + 1,
                fileId: item.id, //id comes from sheetId, so will be used as fileId
                name: item.name,
                distributor: item.distributor,
                previewRows: item.preview_rows,
                folderId: driveFolderId,
                approved: item.approved,
                groupName: ""
              }
              if ("header_mapping" in item && item["header_mapping"]) {
                file.premappedHeaders = item["header_mapping"]
              }

              return file
            })
            .sort((a: ReportUploadRequestFile, b: ReportUploadRequestFile) => {
              const primary = a?.distributor?.localeCompare(b?.distributor)
              if (primary !== 0) return primary

              return a.name.localeCompare(b.name)
            })
          const markedFiles = groupFilesByDistributorAndHeader(spreadSheets)
          setFiles(markedFiles)
          setLoading(false)
        }
      })
      .catch((err) => {
        setLoading(false)
        enqueueSnackbar(`Error on fetching sheets.`, {
          variant: "error",
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "right" }
        })
      })
  }

  const disapproveRequest = (reportUploadRequest: ReportUploadRequest) => {
    if (!(reportUploadRequest && reportUploadRequest.id)) return
    setLoadingApprove(true)
    axios
      .put(`/reports/report-upload-request/update_status/`, {
        id: reportUploadRequest.id,
        status: ReportUploadRequestStatus.REQUEST_DISAPPROVED
      })
      .then((resp) => {
        if (resp.data.success) {
          enqueueSnackbar(`Request Disapproved Successfully`, {
            variant: "success",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
          setReportUploadRequest({ ...reportUploadRequest, status: ReportUploadRequestStatus.REQUEST_DISAPPROVED })
          const updateReportUploadRequestList = [...reportUploadRequestList].map((rur) =>
            reportUploadRequest.id === rur.id ? { ...rur, status: ReportUploadRequestStatus.REQUEST_DISAPPROVED } : rur
          )
          setReportUploadRequestList(updateReportUploadRequestList)
        } else {
          enqueueSnackbar(`An error occurred, please try again!`, {
            variant: "error",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
        }
        setConfirmType(undefined)
        setLoadingApprove(false)
      })
      .catch((err) => {
        enqueueSnackbar(`An error occurred, please try again!`, {
          variant: "error",
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "right" }
        })
        setConfirmType(undefined)
        setLoadingApprove(false)
      })
  }

  const initializeCrawler = (artistId: string, isrcList: string, royaltyReportId: number) => {
    axios
      .post(`/reports/artist/initialize_crawler/?artist_id=${artistId}`, {
        isrc_list: isrcList,
        royalty_report_id: royaltyReportId
      })
      .then((resp) => {
        if (resp.data.success) {
          enqueueSnackbar(`SAA initialized.`, {
            variant: "success",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
        } else {
          enqueueSnackbar(`Request Already Exist.`, {
            variant: "error",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
        }
        setLoading(false)
        setConfirmType(undefined)
      })
      .catch((err) => {
        setLoading(false)
        enqueueSnackbar(`Request Already Exist.`, {
          variant: "success",
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "right" }
        })
        setConfirmType(undefined)
      })
  }

  const delay = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const approveReports = async (fileIdsToApprove: string[]) => {
    if (!reportUploadRequest) return
    const artistName = reportUploadRequest.artist?.name || ""
    const artistId = reportUploadRequest.spotifyId
    const type = reportUploadRequest.type
    const reportUploadRequestId = reportUploadRequest.id
    const data = files
      .filter((file) => fileIdsToApprove.includes(file.fileId))
      .map((file) => ({
        sheet_id: file.fileId,
        sheet_name: file.name,
        distributor: file.distributor,
        matched_headers: matchedHeaders[file.groupName]
      }))
    setLoadingApprove(true)
    axios
      .post(
        `/reports/royalty/upload/?artist_id=${artistId}&artist_name=${artistName}&type=${type}&report_upload_request_id=${reportUploadRequestId}`,
        data
      )
      .then(async (resp) => {
        const result = resp.data.result
        const isrcList = result["isrc_list"]
        const royaltyReportId = result["royalty_report_id"]
        const validIsrc = result["valid_isrc"]
        if (validIsrc && !resp.data.result.error) {
          setReportUploadRequest({ ...reportUploadRequest, status: ReportUploadRequestStatus.REQUEST_APPROVED })
          const updateReportUploadRequestList = [...reportUploadRequestList].map((rur) =>
            reportUploadRequestId === rur.id ? { ...rur, status: ReportUploadRequestStatus.REQUEST_APPROVED } : rur
          )
          setReportUploadRequestList(updateReportUploadRequestList)
          enqueueSnackbar(`Offer report created successfully.`, {
            variant: "success",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
          enqueueSnackbar(`The process will continue in 10 seconds. Please do not close the page.`, {
            variant: "info",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            autoHideDuration: 10500
          })
          setConfirmType(undefined)
          setLoadingApprove(false)
          await delay(10000)
          initializeCrawler(artistId, isrcList, royaltyReportId)
        } else {
          const message = resp.data.result.error || "Error on approving reports. Please check files and try again."
          enqueueSnackbar(message, {
            variant: "error",
            preventDuplicate: true,
            anchorOrigin: { vertical: "top", horizontal: "right" }
          })
          setConfirmType(undefined)
          setLoadingApprove(false)
        }
      })
      .catch((err) => {
        enqueueSnackbar(`Error on approving reports. Please check files and try again.`, {
          variant: "error",
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "right" }
        })
        setConfirmType(undefined)
        setLoadingApprove(false)
      })
  }

  const groupFilesByDistributorAndHeader = (files: ReportUploadRequestFile[]) => {
    const groupMap: Record<string, string> = {}
    const groupList: string[] = []

    let counter = 1

    files.forEach((file) => {
      const distributor = file.distributor
      const headers = file.previewRows?.[0] || []
      const headerKey = JSON.stringify(headers).trim()

      // Unique group key
      const uniqueKey = `${distributor}||${headerKey}`

      // If this combination has not been assigned a group yet → create one
      if (!groupMap[uniqueKey]) {
        groupMap[uniqueKey] = `G${counter}`
        counter++
      }

      // Assign the group name to the file
      file.groupName = groupMap[uniqueKey]
      if (!groupList.includes(groupMap[uniqueKey])) {
        groupList.push(groupMap[uniqueKey])
      }
    })
    const matchedHeaderMap: { [key: string]: typeof initialMatchedHeaders } = {}
    const preMappedGroupList: string[] = []
    groupList.forEach((groupName) => {
      const fileWithPremappedHeaders = files.find((file) => file.groupName === groupName && file.premappedHeaders)
      if (fileWithPremappedHeaders) {
        matchedHeaderMap[groupName] = fileWithPremappedHeaders.premappedHeaders as typeof initialMatchedHeaders
        preMappedGroupList.push(groupName)
      }
    })
    setPreMappedGroupList(preMappedGroupList)
    setPreMappedMatchedHeaders({ ...matchedHeaderMap })
    return files
  }

  useEffect(() => {
    if (!reportUploadRequest?.driveFolderId) return
    fetchFiles(reportUploadRequest?.driveFolderId)
  }, [reportUploadRequest])

  const onClickName = (fileId: string) => {
    const clickedFile = files.find((file: ReportUploadRequestFile) => file.fileId === fileId)
    if (clickedFile) {
      setSelectedFile(clickedFile)
    }
  }

  const columns = useMemo(() => {
    const groupList = Array.from(new Set(files.map((f) => f.groupName))).sort()
    return reportsTableColumns(onClickName, selectedFile?.fileId, groupList, files, selectedFileIds, setSelectedFileIds)
  }, [files, selectedFile, selectedFileIds])

  const fSize = useMemo(() => {
    let fontSize = 13
    if (selectedFile?.previewRows && selectedFile?.previewRows.length > 0) {
      if (selectedFile.previewRows[0].length > 40) fontSize = 10
      else if (selectedFile.previewRows[0].length > 30) fontSize = 11
      else if (selectedFile.previewRows[0].length > 20) fontSize = 12
    }
    return fontSize
  }, [selectedFile])

  useEffect(() => {
    const groupList = Array.from(new Set(files.map((f) => f.groupName))).sort()
    const matchedHeadersObject: { [key: string]: typeof initialMatchedHeaders } = {}
    const headerOptionsMap: { [key: string]: KSelectOption[] } = {}

    groupList.forEach((groupName: string) => {
      const chosenFile = files.find((f) => f.groupName === groupName)
      if (!(chosenFile && chosenFile?.previewRows && chosenFile?.previewRows.length > 0)) return
      const headers = chosenFile.previewRows[0]
      const headerOptions = headers
        .filter((h) => Boolean(h) && h !== "distributor_name")
        .map((header: string, index: number) => ({ value: index, label: header, value2: header }))
      headerOptionsMap[groupName] = headerOptions

      if (!preMappedGroupList.includes(groupName)) {
        // if the headers already mapped with the premappedHeaders that comes with file, do not map it again
        const matchedHeadersMap: typeof initialMatchedHeaders = { ...initialMatchedHeaders }
        Object.values(ReportUploadColumnTypes).forEach((type) => {
          //type is the name the one of the 14 columns we need to match with a column name from the report
          const reportUploadColumnList = reportUploadColumnsMap?.[type] || [] // [header1, header2, ... ]
          if (!reportUploadColumnList) return

          const matchedHeader = headers.find((h) => reportUploadColumnList.includes(h))
          matchedHeadersMap[type] = matchedHeader || ""
        })
        matchedHeadersObject[groupName] = matchedHeadersMap
      }
    })

    setMatchedHeaders({ ...matchedHeadersObject, ...preMappedMatchedHeaders })
    setSelectOptions({ ...headerOptionsMap })
  }, [files, preMappedMatchedHeaders, preMappedGroupList])

  const mandatoryFields: string[] = [
    ReportUploadColumnTypes.PERIOD,
    ReportUploadColumnTypes.QUANTITY,
    ReportUploadColumnTypes.NET_PROGRESS
  ]

  const renderMatchedHeadersComponent = (groupName: string) => {
    if (!groupName) return <div />

    const groupList = Array.from(new Set(files.map((f) => f.groupName))).sort()
    return (
      <div className="flex flex-col gap-2 mb-10 ml-4 pt-10 pb-10 px-4">
        <div className="flex mb-5">
          <KTitleSpan text="Column Header Mapping" fontSize={16} color="#1D2630" lineHeight="20px" />
        </div>
        <div className="flex flex-row gap-1">
          {groupList?.map((groupName: string) => {
            return (
              <div
                key={`report-upload-request-tab-value-${groupName}`}
                className="cursor-pointer mr-8 mb-4 no-wrap"
                style={{ borderBottom: selectedFile?.groupName === groupName ? "1px solid black" : "" }}
                onClick={() => {
                  const firstFile = files.find((file: ReportUploadRequestFile) => file.groupName === groupName) // select the first file wihch belongs the selected group
                  if (firstFile) {
                    setSelectedFile(firstFile)
                  }
                }}
              >
                <KSpan
                  text={groupName}
                  color={groupName === selectedFile?.groupName ? "#000" : "#737373"}
                  fontWeight={groupName === selectedFile?.groupName ? 600 : 400}
                />
              </div>
            )
          })}
        </div>
        <div className="flex flex-row gap-4 w-full flex-wrap">
          <div className="flex flex-row gap-x-6 gap-y-2 w-full max-w-[1000px] flex-wrap">
            {matchedHeaders?.[groupName] &&
              Object.entries({ ...matchedHeaders }?.[groupName] || {})
                .filter(([key]) => key !== "description")
                .sort(([aKey], [bKey]) => aKey.localeCompare(bKey))
                .map(([key, entryValue]) => {
                  return (
                    <div
                      key={`${groupName}-${key}`}
                      className="flex flex-row items-center gap-2 w-auto px-1 justify-between"
                    >
                      <div className="w-[150px] flex items-center">
                        <KSpan text={`${key}: `} fontWeight={600} color="#000" />
                        {mandatoryFields.includes(key) && <KSpan text="*" color="#F00" />}
                      </div>
                      <div className="w-[250px]">
                        <KDropdown
                          key={`${entryValue || Math.random()}`}
                          placeholder="Select column header name"
                          options={selectOptions[groupName]}
                          isClearable={true}
                          isEllipsis={true}
                          menuWidth={240}
                          selected={selectOptions[groupName].find((option) => option.value2 === entryValue)}
                          onSelect={(selected) => {
                            if (!selected) {
                              setMatchedHeaders({
                                ...matchedHeaders,
                                [groupName]: { ...matchedHeaders[groupName], [key]: "" }
                              })
                              return
                            }
                            const selectedValue = (selected as KSelectOption)?.value2
                            const existsInGroup = Object.entries(matchedHeaders[groupName]).some(
                              ([entryKey, val]) => val === selectedValue && entryKey !== key
                            )
                            if (existsInGroup) {
                              enqueueSnackbar("This header is already mapped to another column.", {
                                variant: "warning",
                                anchorOrigin: { vertical: "top", horizontal: "right" }
                              })
                              setMatchedHeaders({
                                ...matchedHeaders,
                                [groupName]: { ...matchedHeaders[groupName], [key]: "" }
                              })
                              return
                            }
                            setMatchedHeaders({
                              ...matchedHeaders,
                              [groupName]: { ...matchedHeaders[groupName], [key]: selectedValue }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
            <div
              key={`${groupName}-selected-currency`}
              className="flex flex-row items-center gap-2 w-auto px-1 justify-between"
            >
              <div className="w-[150px] flex items-center">
                <KSpan text={`${"selected_currency:"} `} fontWeight={600} color="#000" />
              </div>
              <div className="w-[250px]">
                <KDropdown
                  isClearable={true}
                  placeholder="Select currency value"
                  options={currencyOptions}
                  selected={currencyOptions.find(
                    (option) => option.value2 === matchedHeaders[groupName].selected_currency
                  )}
                  onSelect={(selected) => {
                    if (!selected) {
                      setMatchedHeaders({
                        ...matchedHeaders,
                        [groupName]: { ...matchedHeaders[groupName], selected_currency: "" }
                      })
                      return
                    }
                    const selectedValue = (selected as KSelectOption)?.value2
                    setMatchedHeaders({
                      ...matchedHeaders,
                      [groupName]: { ...matchedHeaders[groupName], selected_currency: selectedValue || "" }
                    })
                  }}
                />
              </div>
              <div>
                <KTooltip
                  content={
                    <div className="w-[300px]">
                      <KSpan
                        text="Instead of using the currency header mapping, you can directly select the currency from here.
                        If you select both a currency header and a value here, the header will be ignored."
                        color="#111"
                      />
                    </div>
                  }
                >
                  <img src={InfoIcon} style={{ width: 20, height: 20, minWidth: 20, minHeight: 20 }} />
                </KTooltip>
              </div>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-start">
            <textarea
              value={matchedHeaders?.[groupName]?.description || ""}
              onChange={(e) =>
                setMatchedHeaders({
                  ...matchedHeaders,
                  [groupName]: { ...matchedHeaders[groupName], description: e.target.value }
                })
              }
              style={{
                width: "100%",
                minWidth: 400,
                maxWidth: 700,
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                padding: "10px",
                lineHeight: 1.6,
                flex: 1,
                outlineWidth: 1,
                borderRadius: 6,
                border: "1px solid #666"
              }}
              rows={3}
              placeholder="You can write any necessary notes or explanations about the headers (or header mapping) here."
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <Dialog
      open={reportUploadRequest !== undefined}
      onClose={() => {
        setReportUploadRequest(undefined)
        setSelectedFile(undefined)
        setMatchedHeaders({})
        setPreMappedMatchedHeaders({})
        setSelectedFileIds([])
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen
      className="p-4"
      maxWidth="xl"
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      {selectedFile && selectedFile?.previewRows && (
        <div className="flex">
          <div className="table-container">
            <table className="fancy-table">
              <thead>
                <tr>
                  {selectedFile.previewRows?.length > 0 &&
                    selectedFile.previewRows[0]?.map((header: string, index: number) => {
                      return (
                        <th key={`tr0-${selectedFile.fileId}-${header}-${index}`} style={{ height: 90 }}>
                          <KTitleSpan text={header} fontSize={fSize} color="white" lineHeight="24px" />
                        </th>
                      )
                    })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {selectedFile.previewRows?.length > 1 &&
                    selectedFile.previewRows[1]?.map((value: string, index: number) => {
                      return (
                        <td key={`tr1-${selectedFile.fileId}-${value}-${index}`} style={{ height: 90 }}>
                          <KSpan text={value} fontSize={fSize} color="#111" lineHeight="24px" />
                        </td>
                      )
                    })}
                </tr>
                <tr>
                  {selectedFile.previewRows?.length > 2 &&
                    selectedFile.previewRows[2]?.map((value: string, index: number) => {
                      return (
                        <td key={`tr2-${selectedFile.fileId}-${value}-${index}`} style={{ height: 90 }}>
                          <KSpan text={value} fontSize={fSize} color="#111" lineHeight="24px" />
                        </td>
                      )
                    })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="w-full">{renderMatchedHeadersComponent(selectedFile?.groupName || "")}</div>
      <Grid item xs={12}>
        <MainCard title={"Report Upload Request Sheets"} content={false} sx={{ height: "100%" }}>
          {loading ? (
            <LoaderFullPage />
          ) : (
            <ScrollX>
              <ReactTable columns={columns} data={files} />
            </ScrollX>
          )}
        </MainCard>
      </Grid>
      <div className="flex p-6 mt-8 items-center justify-center gap-3">
        {reportUploadRequest?.status !== ReportUploadRequestStatus.REQUEST_APPROVED &&
          reportUploadRequest?.status !== ReportUploadRequestStatus.REQUEST_DISAPPROVED && (
            <KButton
              width="auto"
              background="#222"
              textColor="#FFF"
              text="Disapprove Request"
              onClick={() => {
                setConfirmType("disapprove")
              }}
            />
          )}
        {selectedFileIds.length > 0 &&
          reportUploadRequest?.status !== ReportUploadRequestStatus.REQUEST_APPROVED &&
          reportUploadRequest?.status !== ReportUploadRequestStatus.REQUEST_DISAPPROVED && (
            <KButton
              width="auto"
              text="Approve Selected Reports"
              onClick={() => {
                setConfirmType("approve")
              }}
            />
          )}
      </div>
      {confirmType !== undefined && (
        <ApproveModal
          confirmType={confirmType}
          setConfirmType={setConfirmType}
          onConfirm={() => {
            if (!reportUploadRequest || loadingApprove) return
            if (confirmType === "disapprove") {
              disapproveRequest(reportUploadRequest)
            } else if (confirmType === "approve") {
              approveReports(selectedFileIds)
            }
          }}
          loading={loadingApprove}
        />
      )}
    </Dialog>
  )
}

export default ReportUploadRequestModal
